/**
 * DealPipelineCustomizationRenderer - Deal-specific pipeline customization.
 *
 * Wraps the generic PipelineCustomizationRenderer with deal-specific columns
 * (progress bar + probability input) and validation. Registered in the action
 * registry for deal pipeline onboarding.
 */

import { useCallback } from 'react';

import type {
  StageColumn,
  StageItem,
  StageValidationErrors,
} from '@/lib/crm/components/stages/StageListEditor';

import type { ActionRendererProps } from '../../../lib/proposals/registry';
import type { StageMutationHelpers } from './PipelineCustomizationRenderer';
import { PipelineCustomizationRenderer } from './PipelineCustomizationRenderer';

// =============================================================================
// Deal-specific column definitions
// =============================================================================

function getProbabilityColor(probability: number): string {
  if (probability >= 70) {
    return 'bg-success-500';
  }
  if (probability >= 40) {
    return 'bg-warning-400';
  }
  return 'bg-gray-300';
}

function createDealColumns(
  helpers: StageMutationHelpers
): StageColumn<StageItem>[] {
  return [
    {
      id: 'progress',
      header: 'Win %',
      // Hidden on mobile so the stage-name column gets the reclaimed space;
      // the editable percentage input to the right of this still carries
      // the actual value, so no information is lost on small screens.
      width: 'hidden sm:block w-12',
      align: 'center',
      renderCell: stage => {
        const probability = (stage.probability as number) ?? 0;
        return (
          <div className='h-1.5 w-12 overflow-hidden rounded-full bg-[var(--color-bg-tertiary)]'>
            <div
              className={`h-full rounded-full transition-all ${getProbabilityColor(probability)}`}
              style={{ width: `${probability}%` }}
            />
          </div>
        );
      },
    },
    {
      id: 'probability',
      header: '',
      width: 'w-16',
      renderCell: stage => {
        const probability = (stage.probability as number) ?? 0;

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const val = parseInt(e.target.value, 10);
          helpers.updateField(
            stage.id,
            'probability',
            isNaN(val) ? 0 : Math.max(0, Math.min(100, val))
          );
        };

        const handleBlur = () => {
          helpers.markTouched(stage.id, 'probability');
        };

        return (
          <div className='relative w-16'>
            <input
              type='number'
              min='0'
              max='100'
              value={probability}
              onChange={handleChange}
              onBlur={handleBlur}
              className='w-full bg-[var(--color-bg-secondary)] text-xs font-medium text-[var(--color-text-secondary)] text-center border rounded-md py-1 px-1 pr-4 outline-none focus:ring-1 border-[var(--color-border-primary)] focus:ring-brand-500 focus:border-brand-500'
            />
            <span className='absolute right-1.5 top-1 text-xs text-fg-quaternary pointer-events-none'>
              %
            </span>
          </div>
        );
      },
      renderDragPreview: stage => {
        const probability = (stage.probability as number) ?? 0;
        return (
          <span className='text-xs text-fg-secondary'>{probability}%</span>
        );
      },
    },
  ];
}

// =============================================================================
// Deal-specific defaults and validation
// =============================================================================

const DEAL_STAGE_DEFAULTS: Record<string, unknown> = { probability: 0 };

function validateDealStage(
  stage: StageItem
): StageValidationErrors[string] | null {
  const probability = stage.probability as number | undefined;
  if (probability !== undefined && (probability < 0 || probability > 100)) {
    return { probability: 'Must be 0–100%' };
  }
  return null;
}

function renderDealReadonlyExtra(stage: StageItem) {
  const probability = (stage.probability as number) ?? 0;
  return (
    <span className='text-xs font-medium text-fg-tertiary'>{probability}%</span>
  );
}

// =============================================================================
// Component
// =============================================================================

export function DealPipelineCustomizationRenderer(props: ActionRendererProps) {
  const createColumns = useCallback(
    (helpers: StageMutationHelpers) => createDealColumns(helpers),
    []
  );

  return (
    <PipelineCustomizationRenderer
      {...props}
      createColumns={createColumns}
      newStageDefaults={DEAL_STAGE_DEFAULTS}
      validateStage={validateDealStage}
      renderReadonlyExtra={renderDealReadonlyExtra}
    />
  );
}
